import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Skeleton,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import PriceSection from "pages/OrderModal/components/PriceSection";
import ConfirmModal from "components/ConfirmModal";
import { useCancelOrderMutation, useGetOrderDetailsQuery } from "store/api";
import { useAppSelector } from "store/hooks/useAppSelector";
import {
  findCancelOrderResult,
  findOrderStatus,
  hasSomeMinutesPassed,
  initialAddressSteps,
  initialCarInfo,
  initialStatus,
  orderCloseReason,
  parseCarInfo,
  timeToCloseOrder,
} from "./constants";
import { setInitialTaxiClass } from "store/slices/taxiClass";
import {
  CustomStepIconProps,
  IAddressStep,
  ICarInfo,
  IOrderCloseReason,
} from "./types";
import FiberManualRecordOutlinedIcon from "@mui/icons-material/FiberManualRecordOutlined";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import { ReactComponent as CarIcon } from "assets/icons/car-icon.svg";
import { formatDateTime } from "utils/formatDateTime";
import { removeCost, setCost } from "store/slices/orderCost";
import { useAppDispatch } from "store/hooks/useAppDispatch";
import useNotificationToast from "hooks/useNotificationToast/useNotificationToast";
import { removeOrderDetails, setStatus } from "store/slices/orderDetails";
import {
  removeComment,
  removeEntrance,
  removeSrcAddress,
  removeSrcAddressRoute,
  removeSrcBuilding,
} from "store/slices/srcAddress";
import { clearBoxes, setNoRoutes } from "store/slices/dstAddress";
import {
  setCheckedOptions,
  setCustomCheckedOptions,
} from "store/slices/options";
import { removeArriveTime } from "store/slices/arriveTime";
import { setDefaultPayment } from "store/slices/payment";
import { IOrderStatus } from "pages/OrderModal/components/DetailsSection/types";
import { getAddressSteps } from "pages/OrderModal/components/SearchModal/constants";
import { shallowEqual } from "react-redux";
import { setTariffs } from "store/slices/tariffs";
import styles from "./CreatedOrder.styled";

export const CustomStepIcon: React.FC<CustomStepIconProps> = ({ isLast }) => {
  if (isLast) {
    return <FmdGoodOutlinedIcon color="error" />;
  }
  return <FiberManualRecordOutlinedIcon color="success" />;
};

const CreatedOrder = () => {
  const orderId = useAppSelector(
    (state) => state.orderDetails.id,
    shallowEqual,
  );
  const accentColor = useAppSelector(
    (state) => state.settings.config.accentColor,
    shallowEqual,
  );
  const cost = useAppSelector(
    (state) => state.orderCost.costDetails,
    shallowEqual,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isCloseOrder, setIsCloseOrder] = useState<boolean>(false);
  const [orderStatus, setOrderStatus] = useState<IOrderStatus>(initialStatus);
  const [closeReason, setCloseReason] = useState<IOrderCloseReason | null>(
    null,
  );
  const [addressSteps, setAddressSteps] =
    useState<IAddressStep[]>(initialAddressSteps);
  const [carInfo, setCarInfo] = useState<ICarInfo>(initialCarInfo);
  const [firstRequestTime, setFirstRequestTime] = useState<string>("");
  const {
    data: orderDetails,
    isLoading,
    isSuccess,
  } = useGetOrderDetailsQuery(orderId || "", {
    skip: !orderId,
    pollingInterval: 5000,
  });
  const dispatch = useAppDispatch();
  const [cancelOrder] = useCancelOrderMutation();

  useEffect(() => {
    if (isSuccess && !firstRequestTime) {
      const localTime = new Date().toLocaleString("en-US", {
        timeZone: "Europe/Kiev",
      });
      setFirstRequestTime(localTime);
    }
  }, [isSuccess, firstRequestTime]);

  useEffect(() => {
    if (orderDetails) {
      dispatch(setStatus(orderDetails.execution_status));
      const status = findOrderStatus(orderDetails.execution_status);
      if (status) {
        setOrderStatus(status);
      }
      const steps = getAddressSteps(orderDetails.route);
      setAddressSteps(steps);
      if (!cost.order_cost) {
        dispatch(setCost(orderDetails.order_cost));
      }
      if (orderDetails.order_car_info) {
        setCarInfo(parseCarInfo(orderDetails.order_car_info));
      }
      const closeReason = orderCloseReason.find(
        (reason) => orderDetails.close_reason === reason.value,
      );
      if (closeReason) {
        setCloseReason(closeReason);
      }
      if (
        orderCloseReason.some(
          (reason) => orderDetails?.close_reason === reason.value,
        )
      ) {
        setIsCloseOrder(true);
      }
      if (
        orderDetails.required_time &&
        hasSomeMinutesPassed(orderDetails.required_time, timeToCloseOrder)
      ) {
        setIsCloseOrder(true);
      }
    }
  }, [orderDetails]);

  useEffect(() => {
    if (isCloseOrder) {
      setTimeout(() => {
        dispatch(removeOrderDetails());
        dispatch(removeSrcAddress());
        dispatch(removeSrcAddressRoute());
        dispatch(removeEntrance());
        dispatch(removeComment());
        dispatch(removeSrcBuilding());
        dispatch(clearBoxes());
        dispatch(removeArriveTime());
        dispatch(setDefaultPayment());
        dispatch(setNoRoutes(false));
        dispatch(setCheckedOptions([]));
        dispatch(setCustomCheckedOptions([]));
        dispatch(setInitialTaxiClass());
        dispatch(setTariffs([]));
        dispatch(removeCost());
        setIsCloseOrder(false);
      }, 10000);
    }
  }, [isCloseOrder]);

  const handleOrderCancel = async () => {
    if (orderId) {
      try {
        await cancelOrder(orderId).unwrap();
        dispatch(removeOrderDetails());
        dispatch(removeSrcAddressRoute());
        dispatch(removeSrcAddress());
        dispatch(removeSrcBuilding());
        dispatch(removeEntrance());
        dispatch(removeComment());
        dispatch(removeArriveTime());
        dispatch(clearBoxes());
        dispatch(setNoRoutes(false));
        dispatch(setCheckedOptions([]));
        dispatch(setCustomCheckedOptions([]));
        dispatch(setInitialTaxiClass());
        dispatch(setDefaultPayment());
        dispatch(removeCost());
        dispatch(setTariffs([]));
      } catch (error) {
        useNotificationToast({
          type: "error",
          message: findCancelOrderResult(error.data.order_client_cancel_result)
            ?.description,
        });
      }
    }
  };

  const handleToggleModal = (): void => {
    setIsModalOpen((prev) => !prev);
  };

  return (
    <Box sx={styles.contentWrap}>
      {isLoading ? (
        <Skeleton />
      ) : (
        <>
          <Typography variant="h2">{orderStatus.title}</Typography>
          {closeReason && (
            <Typography variant="subtitle1" sx={styles.subtitle}>
              {`(${closeReason.description})`}
            </Typography>
          )}
        </>
      )}
      {isSuccess && orderDetails.order_car_info ? (
        <Box sx={styles.carWrap}>
          <Box sx={styles.carInfoWrap(accentColor)}>
            <CarIcon />
            <Box sx={styles.carInfo}>
              <Typography>{carInfo.number}</Typography>
              <Typography>{`${carInfo.name}, ${carInfo.color}`}</Typography>
            </Box>
          </Box>
          <Link href={`tel:${orderDetails.driver_phone}`}>
            <IconButton aria-label="delete" size="large">
              <PhoneIcon color="success" />
            </IconButton>
          </Link>
        </Box>
      ) : (
        <Box sx={styles.carInfoWrap(accentColor)}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={styles.skeleton}>
            <Skeleton />
            <Skeleton />
          </Box>
        </Box>
      )}
      <Divider />
      <Stepper orientation="vertical" sx={styles.stepper}>
        {addressSteps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel
              StepIconComponent={() => (
                <CustomStepIcon isLast={index === addressSteps.length - 1} />
              )}
              sx={styles.stepLabel}
            >
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      <Divider />
      <Box sx={styles.timeWrap}>
        <Typography>Час:</Typography>
        {isSuccess && (
          <Typography sx={styles.time}>{`${
            orderDetails.required_time
              ? formatDateTime(orderDetails.required_time)
              : "як можна скоріше"
          }`}</Typography>
        )}
      </Box>
      <Box sx={styles.footer(!!orderId)}>
        {isSuccess && <PriceSection />}
        <Button
          type="button"
          className="secondary"
          sx={styles.btn}
          onClick={handleToggleModal}
          disabled={orderCloseReason.some(
            (reason) => orderDetails?.close_reason === reason.value,
          )}
        >
          Скасувати
        </Button>
      </Box>
      <ConfirmModal
        openModal={isModalOpen}
        modalClose={handleToggleModal}
        handleAction={handleOrderCancel}
        titleMessage="Скасувати замовлення?"
        buttonMessage="Так"
        secondBtnMessage="Ні"
      />
    </Box>
  );
};

export default CreatedOrder;
